import groupBy from 'lodash.groupby'
import { useEffect, useMemo, useState } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import {
  StringParam,
  useQueryParams,
  withDefault,
  ArrayParam,
  JsonParam,
  NumberParam,
} from 'use-query-params'
import {
  Checkbox,
  useMessage,
  useGetTeamMembers,
  Spin,
  usePaginatedAssignmentList,
  useGlobalState,
} from '@pepper/ui'

import { useOrderItems } from 'utils/hooks/useOrderItems'
import { useSelectedCompanyId } from 'utils/functions/misc'
import downloadItems from 'utils/api/downloadItems'
import { useGetWorkflowStatus } from 'utils/hooks/workflows/useGetWorkflowStatus'
import Header from 'components/items/Header/Header'
import AssignmentRow from 'components/items/AssignmentRow/AssignmentRow'
import Footer from 'components/items/Footer/Footer'
import BottomPopup from 'components/items/OrdersBottomPopup/OrdersBottomPopup'
import DraftBanner from 'components/drafts/DraftBanner/DraftBanner'
import EmptyState from './EmptyState'
import { sortOptions } from '../Header/schema'

import s from './AssignmentList.module.css'

type Props = {
  viewType: 'project-items' | 'all-items'
  // * * pass ids of newly created private_docs items
  recentItems?: string[]
  project?: Project
  draftsCount?: number
  toggleDraftsView?: () => void
  handleDocsDownload?: (items: Assignment[]) => void
}

const PROJECT_PER_PAGE = 10

const List = ({
  viewType,
  recentItems,
  handleDocsDownload,
  project,
  draftsCount = 0,
  toggleDraftsView,
}: Props) => {
  const [selectedItemsStage, setSelectedItemsStage] = useState<
    AssignmentStage | null | ''
  >(null)
  const { permissions } = useGlobalState()
  const [selectedItems, setSelectedItems] = useState<Assignment[]>([])
  const [selectedItem, setSelectedItem] = useState<Assignment>()
  const companyId = useSelectedCompanyId()
  const { showError } = useMessage()
  const { show: showServiceDrawer } = useModal('service-selection-drawer')
  const { data: assignees } = useGetTeamMembers({ companyId })
  const { data: workflowStatuses } = useGetWorkflowStatus({ companyId })

  const [queryParamFilters, setQueryParamFilters] = useQueryParams({
    stage: withDefault(ArrayParam, undefined),
    rework: withDefault(StringParam, undefined),
    vertical: withDefault(ArrayParam, undefined),
    status: withDefault(ArrayParam, []),
    assignee: withDefault(ArrayParam, []),
    view_assignment: withDefault(StringParam, undefined),
    project_title: withDefault(StringParam, undefined),
    sort: withDefault(JsonParam, {
      sort_by: sortOptions[0].sortBy,
      sort_order: sortOptions[0].sortOrder,
    }),
    currentPage: withDefault(NumberParam, 0),
    limit: withDefault(NumberParam, PROJECT_PER_PAGE),
    tab: withDefault(StringParam, undefined),
  })
  const { stage, rework, vertical, assignee, status } = queryParamFilters
  const isNoFilterApplied =
    assignee.length === 0 &&
    status.length === 0 &&
    !stage &&
    !rework &&
    !vertical
  const {
    records: items,
    total: totalOrders,
    refetch: refetchOrders,
    isLoading: isOrdersLoading,
  } = useOrderItems({
    companyId,
    projectId: project ? project.id : '',
    queryParams: queryParamFilters,
    pageSize: queryParamFilters.limit,
    options: {
      isEnabled: true,
    },
  })
  const { records: assignments } = usePaginatedAssignmentList(
    {
      project_id: project?.id,
      favourite_creator: true,
      include_extra: true,
    },
    1,
    false,
    false,
    !!project && permissions?.cmp
  )

  const itemsOrder = items.map(item => item.assignment_code)

  const _getGroupedItems = (_items: Assignment[]): Assignment[] => {
    const listItems: Assignment[] = []
    const group = groupBy(_items, 'group_id')

    Object.keys(group).forEach(groupId => {
      listItems.push(
        ...group[groupId].map(item => {
          return {
            ...item,
            grouped: groupId !== 'null' && group[groupId].length > 1,
          }
        })
      )
    })
    const listItemsMap = groupBy(listItems, 'assignment_code')
    const sortedListItems = itemsOrder.map(itemCode => {
      return listItemsMap[itemCode][0]
    })

    return sortedListItems
  }

  const firstAssignment = assignments[0]

  const handleDownload = (_items: Assignment[]) => {
    try {
      downloadItems(_items, companyId, 'order')
    } catch (error) {
      showError(error.message)
    }
  }

  const handleOnRowClick = (id: string, title?: string) => {
    title
      ? setQueryParamFilters({ view_assignment: id, project_title: title })
      : setQueryParamFilters({
          view_assignment: id,
          project_title: `Items in ${project?.project_title}`,
        })
  }

  const handleSelectAll = (isChecked: boolean) => {
    isChecked ? setSelectedItems([]) : setSelectedItems(items)
  }

  const handleOrderContent = () => {
    showServiceDrawer({
      tab: 'marketplace',
      projectId: project?.id,
      projectBody: {
        project_title: project?.project_title,
        document_brief_id: firstAssignment?.document_brief?.id,
        genre_id: firstAssignment?.genre.id,
        language_dialect: firstAssignment?.extra?.language_dialect,
        sub_genre_id: firstAssignment?.sub_genre?.id,
      },
    })
  }

  useEffect(() => {
    if (selectedItems.length > 1) return
    if (selectedItems.length === 1) {
      setSelectedItemsStage(selectedItems[0].stage)
      return
    }
    setSelectedItemsStage(null)
  }, [selectedItems])

  useEffect(() => {
    // Update the selected item if items list if updated
    if (!selectedItem) return
    const _item = items.find(_i => _i.id === selectedItem.id)
    if (_item) {
      setSelectedItem(_item)
    }
  }, [items])

  const getApprovedAssignments = () => {
    return (
      items
        .filter(item => item.stage === 'approved')
        // filtering out text category with pepper docs
        .filter(
          item =>
            item.category.key === 'text' &&
            item.submission_file?.src_type === 'pepper_editor'
        )
    )
  }

  useEffect(() => {
    setQueryParamFilters({ limit: queryParamFilters.limit })
  }, [])

  const groupedItems: Assignment[] = useMemo(() => {
    return _getGroupedItems(items)
  }, [items, _getGroupedItems, itemsOrder])

  const isEmptyState = items.length === 0 && isNoFilterApplied

  const renderEmptyList = isEmptyState ? (
    <div className={s.emptyList}>
      <EmptyState onOrderContent={handleOrderContent} />
    </div>
  ) : (
    <div className={s.emptyList}>No results found</div>
  )

  if (isOrdersLoading) return <Spin className={s.spin} size="large" />

  return (
    <>
      <Header<Assignment>
        viewType={viewType}
        onDownload={() => handleDownload(items)}
        setSelectedItems={setSelectedItems}
        tabName="orders"
        project={project}
        onOrderClick={handleOrderContent}
        showFilter={!isEmptyState}
        handleDocsDownload={() => {
          handleDocsDownload?.(getApprovedAssignments())
        }}
        isApprovedDocsListEmpty={getApprovedAssignments().length === 0}
      />
      {project && !queryParamFilters.tab && draftsCount > 0 && (
        <DraftBanner
          totalDrafts={draftsCount}
          onShowAllDrafts={() => toggleDraftsView?.()}
        />
      )}
      {groupedItems.length > 0 ? (
        <div className={s.list}>
          <div className={s.wrapper}>
            <Checkbox
              disabled={viewType === 'all-items'}
              className={s.checkbox}
              onChange={() =>
                handleSelectAll(selectedItems.length === items.length)
              }
              checked={selectedItems.length === items.length}
            />
            Content Items
          </div>
          {groupedItems?.map((item: Assignment) => (
            <AssignmentRow
              key={item.id}
              item={item}
              companyId={companyId}
              selectedItems={selectedItems}
              selectedItemsStage={selectedItemsStage}
              onClick={handleOnRowClick}
              onDownload={handleDownload}
              refetchItems={refetchOrders}
              setSelectedItems={setSelectedItems}
              recentItems={recentItems}
              assigneeList={assignees}
              workflowStatusList={workflowStatuses}
            />
          ))}
          <Footer
            total={totalOrders}
            queryParamFilters={queryParamFilters}
            setQueryParamFilters={setQueryParamFilters}
          />
        </div>
      ) : (
        renderEmptyList
      )}

      {selectedItems.length > 0 && (
        <div className={s.footer}>
          <BottomPopup
            items={items}
            onDownload={handleDownload}
            refetchRecords={refetchOrders}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            selectedItemsStage={selectedItemsStage}
            handleDocsDownload={handleDocsDownload}
            companyId={companyId}
            assigneeList={assignees}
            workflowStatusList={workflowStatuses}
          />
        </div>
      )}
    </>
  )
}

export default List
